import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #fd7e14;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #fff3e0, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #fd7e14;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const PassagePreview = styled.div`
  background: #fff8e1;
  border-left: 4px solid #ffc107;
  padding: 12px;
  margin-top: 10px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

const QuestionCard = styled.div`
  border: 2px solid #ff9800;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const AnswerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #28a745;
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #c82333;
  }
`;

const AddButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;
  
  &:hover {
    background: #218838;
  }
`;

interface Question {
  id: string;
  question_text: string;
  correct_answers: string[];
}

interface ContentData {
  passage: string;
  questions: Question[];
}

interface ReadingEssayFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const ReadingEssayForm: React.FC<ReadingEssayFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      // Check if it's the new format with questions array
      if (parsed?.questions && Array.isArray(parsed.questions)) {
        // Deep clone to ensure each question has independent correct_answers array
        return {
          ...parsed,
          questions: parsed.questions.map((q: any) => ({
            ...q,
            correct_answers: Array.isArray(q.correct_answers) ? [...q.correct_answers] : []
          }))
        };
      }
      
      // Convert old format to new format
      if (parsed?.question) {
        return {
          passage: parsed.passage || '',
          questions: [{
            id: Date.now().toString(),
            question_text: parsed.question,
            correct_answers: []
          }]
        };
      }
      
      // Default new format
      return {
        passage: '',
        questions: [{
          id: Date.now().toString(),
          question_text: '',
          correct_answers: []
        }]
      };
    } catch {
      return {
        passage: '',
        questions: [{
          id: Date.now().toString(),
          question_text: '',
          correct_answers: []
        }]
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handlePassageChange = (value: string) => {
    setContentData((prevData: ContentData) => ({
      ...prevData,
      passage: value,
    }));
  };

  const handleQuestionTextChange = (questionId: string, value: string) => {
    setContentData((prevData: ContentData) => ({
      ...prevData,
      questions: prevData.questions.map((q: Question) =>
        q.id === questionId ? { ...q, question_text: value } : q
      ),
    }));
  };

  const handleAnswerChange = (questionId: string, answerIndex: number, value: string) => {
    setContentData((prevData: ContentData) => {
      const newQuestions = prevData.questions.map((q: Question) => {
        if (q.id === questionId) {
          const newAnswers = [...(q.correct_answers || [])];
          newAnswers[answerIndex] = value;
          return {
            ...q,
            correct_answers: newAnswers
          };
        }
        return q;
      });
      
      return {
        ...prevData,
        questions: newQuestions
      };
    });
  };

  const addAnswer = (questionId: string) => {
    setContentData((prevData: ContentData) => ({
      ...prevData,
      questions: prevData.questions.map((q: Question) =>
        q.id === questionId
          ? { ...q, correct_answers: [...(q.correct_answers || []), ''] }
          : q
      ),
    }));
  };

  const removeAnswer = (questionId: string, answerIndex: number) => {
    setContentData((prevData: ContentData) => ({
      ...prevData,
      questions: prevData.questions.map((q: Question) =>
        q.id === questionId
          ? {
              ...q,
              correct_answers: (q.correct_answers || []).filter((_, idx) => idx !== answerIndex),
            }
          : q
      ),
    }));
  };

  const addQuestion = () => {
    setContentData((prevData: ContentData) => ({
      ...prevData,
      questions: [
        ...prevData.questions,
        {
          id: Date.now().toString(),
          question_text: '',
          correct_answers: [],
        },
      ],
    }));
  };

  const removeQuestion = (questionId: string) => {
    if (contentData.questions.length === 1) {
      alert('Phải có ít nhất 1 câu hỏi!');
      return;
    }
    setContentData((prevData: ContentData) => ({
      ...prevData,
      questions: prevData.questions.filter((q: Question) => q.id !== questionId),
    }));
  };


  return (
    <FormBox>
      <h5>📖 Nội dung câu hỏi - Đọc đoạn văn và trả lời câu hỏi TỰ LUẬN</h5>
      
      <FormGroup>
        <Label>Đoạn văn: *</Label>
        <Input
          type="textarea"
          value={contentData.passage || ''}
          onChange={(e) => handlePassageChange(e.target.value)}
          placeholder="Nhập đoạn văn để học sinh đọc...

Ví dụ: Tom lives in a small village near the river. He goes fishing every morning. His favorite hobby is reading books in the library."
          rows={6}
        />
        <small className="form-text text-muted">
          Đoạn văn mà học sinh cần đọc và hiểu
        </small>
      </FormGroup>

      <div style={{ 
        background: '#fff3cd', 
        border: '1px solid #ffc107',
        borderRadius: '4px',
        padding: '10px',
        marginBottom: '15px'
      }}>
        <strong>💡 Hướng dẫn:</strong>
        <ul style={{ marginBottom: 0, paddingLeft: '20px', fontSize: '14px' }}>
          <li>Thêm nhiều câu hỏi liên quan đến đoạn văn</li>
          <li>Mỗi câu hỏi có thể có nhiều đáp án đúng (để hệ thống tự chấm)</li>
          <li>Nếu không thêm đáp án, giáo viên sẽ phải chấm tay</li>
        </ul>
      </div>

      {/* Questions Section */}
      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <Label style={{ marginBottom: 0, fontWeight: 'bold', fontSize: '16px' }}>
            📝 Câu hỏi ({(contentData.questions || []).length})
          </Label>
          <AddButton type="button" onClick={addQuestion}>+ Thêm câu hỏi</AddButton>
        </div>

        {(contentData.questions || []).map((question: Question, qIndex: number) => (
          <QuestionCard key={question.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <strong style={{ color: '#fd7e14' }}>Câu hỏi {qIndex + 1}</strong>
              {(contentData.questions || []).length > 1 && (
                <RemoveButton type="button" onClick={() => removeQuestion(question.id)}>
                  🗑️ Xóa câu hỏi
                </RemoveButton>
              )}
            </div>

            <FormGroup>
              <Label>Nội dung câu hỏi: *</Label>
              <Input
                type="textarea"
                value={question.question_text}
                onChange={(e) => handleQuestionTextChange(question.id, e.target.value)}
                placeholder="Nhập câu hỏi...

Ví dụ: Where does Tom live?"
                rows={2}
              />
            </FormGroup>

            {/* Answer Section */}
            <div style={{ marginTop: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Label style={{ marginBottom: 0, fontSize: '14px', color: '#28a745' }}>
                  ✅ Đáp án đúng ({(question.correct_answers || []).length})
                </Label>
                <AddButton 
                  type="button"
                  style={{ padding: '5px 10px', fontSize: '12px' }}
                  onClick={() => addAnswer(question.id)}
                >
                  + Thêm đáp án
                </AddButton>
              </div>

              {(question.correct_answers || []).length === 0 ? (
                <div style={{ 
                  padding: '10px', 
                  background: '#fff3cd', 
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#856404'
                }}>
                  ⚠️ Chưa có đáp án - Giáo viên sẽ chấm tay
                </div>
              ) : (
                <div style={{ paddingLeft: '10px' }}>
                  {(question.correct_answers || []).map((answer, aIndex) => (
                    <AnswerItem key={`${question.id}-answer-${aIndex}`}>
                      <span style={{ minWidth: '30px', fontWeight: 'bold', color: '#28a745' }}>
                        #{aIndex + 1}
                      </span>
                      <Input
                        type="text"
                        value={answer}
                        onChange={(e) => handleAnswerChange(question.id, aIndex, e.target.value)}
                        placeholder="Nhập đáp án đúng..."
                        style={{ flex: 1 }}
                      />
                      <RemoveButton 
                        type="button"
                        onClick={() => removeAnswer(question.id, aIndex)}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        ✕
                      </RemoveButton>
                    </AnswerItem>
                  ))}
                </div>
              )}
              
              <small className="form-text text-muted" style={{ marginTop: '5px' }}>
                💡 Hệ thống sẽ so sánh câu trả lời của học sinh với các đáp án trên (không phân biệt hoa thường)
              </small>
            </div>
          </QuestionCard>
        ))}
      </div>

      {/* Preview */}
      <div style={{ 
        background: '#ffe0b2', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#e65100' }}>📋 Xem trước bài đọc:</strong>
        {contentData.passage ? (
          <PassagePreview>
            {contentData.passage}
          </PassagePreview>
        ) : (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#999',
            fontStyle: 'italic'
          }}>
            (chưa có đoạn văn)
          </div>
        )}
        
        {(contentData.questions || []).length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <strong style={{ color: '#fd7e14' }}>❓ Các câu hỏi:</strong>
            {(contentData.questions || []).map((q: Question, idx: number) => (
              <div key={q.id} style={{ 
                marginTop: '10px',
                padding: '10px',
                background: 'white',
                borderRadius: '4px',
                borderLeft: '4px solid #fd7e14'
              }}>
                <div><strong>Câu {idx + 1}:</strong> {q.question_text || '(chưa nhập)'}</div>
                {(q.correct_answers || []).length > 0 && (
                  <div style={{ marginTop: '5px', fontSize: '13px', color: '#28a745' }}>
                    <strong>Đáp án:</strong> {(q.correct_answers || []).filter(a => a.trim()).join(' / ') || '(chưa nhập)'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default ReadingEssayForm;
